import { useEffect, useRef, useState } from 'react';
import { loadGoogleIdentity, type GoogleCredentialResponse } from '../../lib/google';

interface GoogleSignInButtonProps {
  /** Called with Google's ID token when the user picks an account. Caller decides signup vs signin. */
  onCredential: (idToken: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  /** Disabled state — covers the "no client id configured" case as well. */
  disabled?: boolean;
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

/**
 * Renders the official Google Identity Services button. Falls back to a clear
 * configuration message if VITE_GOOGLE_CLIENT_ID isn't set.
 */
export function GoogleSignInButton({ onCredential, text = 'continue_with', disabled }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!CLIENT_ID) {
      setError('Google sign-in is not configured (VITE_GOOGLE_CLIENT_ID missing).');
      return;
    }
    let cancelled = false;
    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !containerRef.current) return;
        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp: GoogleCredentialResponse) => {
            if (resp?.credential) onCredential(resp.credential);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 320,
        });
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [onCredential, text]);

  if (error) {
    return (
      <div className="text-xs font-medium text-brand-muted bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
      <div ref={containerRef} className="flex justify-center" />
    </div>
  );
}
